import { Button, Divider, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import
{
   getDetailOrderTaskingByUerAsync,
   getUrlDownloadItemOrderAsync,
   updateStatusOrderAsync,
} from "../../../../store/reducers/orderSlice";

import CommonComponents from "../../../../components/Commons";
import useToggle from "../../../../hooks/useToggle";

import { DownOutlined, RightOutlined } from "@ant-design/icons";
import
{
   flyToGeometry,
   formatValue,
} from "../../../../components/VMRS/ExploreArchive/ultis";
import { AOI_FEATURE_KEY } from "../../../../components/VMRS/MapAdapter/plugins/aoi/consts";
import PolygonInfo from "../../../../components/VMRS/Order/Tasking/PolygonInfo";
import { ESttausCode } from "../../../../enums";
import { getErrorMsg } from "../../../../helpers/common";
import { showError, showSuccess } from "../../../../helpers/toast";
import { setLoadingGlobal } from "../../../../store/reducers/appCommonSlice";
import { addDownloads } from "../../../../store/reducers/downloadSlice";
import { RootState } from "../../../../store/store";
import { downloadManager } from "../../../../utils/downloadManager";


import
{
   dataKeyGeneral,
   formatLabelWithUnit,
} from "../../../../components/VMRS/ExploreTasking/ultis";
import ImagesMapVMRS from "../../../../components/VMRS/MapAdapter/ImagesMap";
import { changeSourceData } from "../../../../components/VMRS/MapAdapter/plugins/draw";
import
{
   clearFeatures,
   setDrawingMode,
   updateFeature
} from "../../../../components/VMRS/MapAdapter/store/slices/drawSlice";
import
{
   changeBasemap,
   setBasemapList,
} from "../../../../components/VMRS/MapAdapter/store/slices/mapSlice";
import { BASEMAPS } from "../../../../components/VMRS/MapAdapter/utils/consts";
import { useOrderRealtimeSync } from "../../../../components/VMRS/Order/hooks/useOrderRealtime";
import CartImageOrder from "../../../../components/VMRS/Order/Tasking/CartImageOrder";
import { ReasonCancelReject } from "../../../../components/VMRS/Order/Tasking/common";
import ImageInfo from "../../../../components/VMRS/ExploreArchive/ImageInfo";
import { clearAOIFeatures, setDrawingAOIMode } from "../../../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { clearAllLayers } from "../../../../components/VMRS/MapAdapter/store/slices/layerSlice";
import { setMeasureMode } from "../../../../components/VMRS/MapAdapter/store/slices/measureSlice";

const Section = ({ title, children, defaultOpen = true }: any) =>
{
   const [open, setOpen] = useState(defaultOpen);

   return (
      <div className="px-6">
         <div className="bg-[var(--neutral-950)] rounded-md p-3 mb-4 shadow">
            {/* HEADER */}
            <div
               className={
                  "flex items-center justify-between cursor-pointer " +
                  (open ? "mb-3" : "")
               }
               onClick={() => setOpen(!open)}
            >
               <div className="flex items-center gap-2 w-full justify-between">
                  <div className="text-[#F5F6FA] text-sm font-semibold tracking-wide uppercase">
                     {title}
                  </div>
                  {open ? (
                     <DownOutlined style={{ fontSize: "12px" }} />
                  ) : (
                     <RightOutlined style={{ fontSize: "12px" }} />
                  )}
               </div>
            </div>

            {/* CONTENT */}
            {open && <div className="text-sm text-slate-200">{children}</div>}
         </div>
      </div>
   );
};

const Row = ({ label, value, highlight, warning }: any) => (
   <div className="flex justify-between py-1 w-full">
      <p className="mb-1 text-des  w-1/3">{label}</p>

      <span
         className={`w-2/3 text-right ${warning
            ? "text-red-500"
            : highlight
               ? "text-cyan-400"
               : "text-slate-200"
            }`}
      >
         {value ? value : "--"}
      </span>
   </div>
);

export default function OrderTaskingDetail()
{
   const { t } = useTranslation();
   const { id } = useParams();
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const location = useLocation();

   const [loading, setLoading] = useState(true);
   const [data, setData] = useState<any>({});
   const [reason, setReason] = useState<string>("");
   const [engineMap, setEngineMap] = useState<maplibregl.Map | undefined>(
      undefined,
   );
   const [selectedView, setSelectedView] = useState<any>(undefined);

   const notifications = useSelector(
      (state: RootState) => state.notificationSlice.total,
   );

   const downloadingFiles = useSelector(
      (state: RootState) => state.download.downloadingFiles,
   );
   const dataStatusOrder = useSelector(
      (state: RootState) => state.appCommonSlice.dataStatusOrder,
   );

   const {
      mapType,
      view: mapView,
      basemaps,
      selectedBasemap,
   } = useSelector((state: RootState) => state.map);


   const getData = async () =>
   {
      try
      {
         setLoading(true);

         let res = // @ts-ignore
            await dispatch(getDetailOrderTaskingByUerAsync(id)).unwrap();

         setData(res?.data?.data ?? {});

         if (res?.data?.data?.items?.[0]?.order_payload?.geometry)
         {
            const geometry = res?.data?.data?.items?.[0]?.order_payload?.geometry;
            const featureId = `${AOI_FEATURE_KEY}-${res?.data?.data?.order_number}`;
            const fill = "rgba(255, 255, 0, 0.9)";
            const stroke = "rgba(255, 255, 0, 1)";

            dispatch(updateFeature({ id: featureId, geometry, fill, stroke }));
            changeSourceData(engineMap, [{ id: featureId, geometry, fill, stroke }]);
            flyToGeometry(geometry, engineMap);
         }
         setLoading(false);
      } catch (error)
      {
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      if (!engineMap || !id) return;

      getData();
   }, [id, engineMap]);


   useEffect(() =>
   {
      return () =>
      {
         dispatch(clearFeatures());
      };
   }, []);

   useEffect(() =>
   {
      const basemaps = BASEMAPS;
      dispatch(setBasemapList(basemaps));
      dispatch(changeBasemap(basemaps[0]));
   }, []);



   const handleChangeStatusOrder = async (
      idOrder: number,
      statusName: string,
      note: string,
      toggleCallback?: () => void,
   ) =>
   {
      try
      {
         setLoading(true);
         dispatch(setLoadingGlobal(true));
         toggleCallback?.();

         const statusId = dataStatusOrder?.find(
            (itemF: any) => itemF?.key === statusName,
         )?.id;

         // @ts-ignore
         const result = await dispatch(
            // @ts-ignore
            updateStatusOrderAsync({
               order_id: id,
               status_id: statusId,
               notes: note,
            }),
         ).unwrap();

         if (result.status === ESttausCode.OK)
         {
            showSuccess(t("Successfully"));

            setData((prev: any) => ({
               ...prev,
               status: {
                  ...prev.status,
                  key: dataStatusOrder?.find(
                     (itemF: any) => itemF?.key === statusName,
                  )?.key,
                  id: dataStatusOrder?.find((itemF: any) => itemF?.key === statusName)
                     ?.id,
                  name: dataStatusOrder?.find(
                     (itemF: any) => itemF?.key === statusName,
                  )?.name,
                  color: dataStatusOrder?.find(
                     (itemF: any) => itemF?.key === statusName,
                  )?.color,
               },
            }));
            // getData();
         }
      } catch (error)
      {
         showError(t(getErrorMsg(error)));
      } finally
      {
         dispatch(setLoadingGlobal(false));
         setLoading(false);
      }
   };

   const {
      open: openCancelOrder,
      toggle: toggleCancelOrder,
      shouldRender: shouldRenderCancelOrder,
   } = useToggle();

   const {
      open: openSendOrderUser,
      toggle: toggleSendOrderUser,
      shouldRender: shouldSendOrderUser,
   } = useToggle();

   const {
      open: openConfirmPrice,
      toggle: toggleConfirmPrice,
      shouldRender: shouldConfirmPrice,
   } = useToggle();

   const {
      open: openCancelPrice,
      toggle: toggleCancelPrice,
      shouldRender: shouldCancelPrice,
   } = useToggle();

   const {
      open: openViewInfo,
      shouldRender: shouldRenderViewInfo,
      toggle: toggleViewInfo,
   } = useToggle();

   const handleSendOrderUser = () =>
   {
      handleChangeStatusOrder(Number(id), "REVIEWING", "", toggleSendOrderUser);
   };

   const handleCancel = () =>
   {
      handleChangeStatusOrder(Number(id), "CANCELED", reason, toggleCancelOrder);
   };

   const handleConfirmPrice = () =>
   {
      handleChangeStatusOrder(
         Number(id),
         "CONFIRMED_QUOTATION",
         "",
         toggleConfirmPrice,
      );
   };

   const handleDownloadData = async () =>
   {
      try
      {
         setLoading(true);
         // @ts-ignore
         const res = await dispatch(
            // @ts-ignore
            getUrlDownloadItemOrderAsync({ order_id: data?.items?.[0]?.id }),
         ).unwrap();

         let files: Array<{
            id: string;
            name: string;
            url: string;
            progress: number;
            status: "downloading" | "paused" | "completed" | "error";
         }> = [];

         if (res?.status === 200 && res?.data?.data)
         {
            const responseData = res?.data?.data;

            // Handle both array and single object formats
            if (Array.isArray(responseData) && responseData.length > 0)
            {
               files = responseData.map((item: any, index: number) => ({
                  id: `file-${Date.now()}-${index}`,
                  name: item.name || `file_${index}`,
                  url: item.url,
                  progress: 0,
                  status: "downloading" as const,
               }));
            } else if (responseData && responseData.url)
            {
               // Single object format (new mock data)
               files = [
                  {
                     id: `file-${Date.now()}`,
                     name: responseData.name || "download_file",
                     url: responseData.url,
                     progress: 0,
                     status: "downloading" as const,
                  },
               ];
            } else
            {
               showError(t("No files available for download"));
               setLoading(false);
               return;
            }

            // Add files to global state
            dispatch(addDownloads(files));

            // Start downloads via manager
            files.forEach((file) =>
            {
               downloadManager.downloadFile(file);
            });
         } else
         {
            showError(t("No files available for download"));
         }
      } catch (error)
      {
         showError(t(getErrorMsg(error)));
      } finally
      {
         setLoading(false);
      }
   };

   const handleCancelPrice = () =>
   {
      handleChangeStatusOrder(
         Number(id),
         "REJECTED_QUOTATION",
         "",
         toggleCancelPrice,
      );
   };

   const handleViewInfo = (item?: any) =>
   {
      setSelectedView(item);
      toggleViewInfo();
   };

   useOrderRealtimeSync({
      id,
      notifications,
      getData,
      engine: engineMap,
   });

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearAllLayers());
      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      handleResetMap();

      return () =>
      {
         handleResetMap();
      };
   }, []);

   return (
      <ContentLayoutManagement
         header={
            <HeaderContent
               breadcrumb={[
                  {
                     title: t("Tasking Orders"),
                     path: `/order/management-tasking${location.search}`,
                  },
                  {
                     title: data?.name,
                  },
               ]}
            />
         }
      >
         <div className="h-full flex bg-[var(--neutral-1000)] text-white">
            <div className="relative w-5/12 flex flex-col border-r border-[#26324a]  pt-6">
               <Spin spinning={loading}>
                  <div
                     className=" overflow-auto"
                     style={{
                        height:
                           data?.status?.key === "NEWORDER" ||
                              data?.status?.key === "CONFIRMING_QUOTATION" ||
                              data?.status?.key === "CLOSED"
                              ? "calc(100% - 65px)"
                              : "100%",
                     }}
                  >
                     {/* Status + Info */}
                     <div className="grid grid-cols-3 px-6 gap-2 mb-4">
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Status")}
                           </div>
                           <div
                              className=" text-sm font-medium p-1 rounded"
                              style={{
                                 background: data?.status?.color ?? "#86F988",
                                 fontSize: "14px",
                                 color: "black",
                              }}
                           >
                              {t(data?.status?.name)}
                           </div>
                        </div>
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Provider")}
                           </div>
                           <div className="text-sm">
                              {data?.items?.[0]?.provider?.name}
                           </div>
                        </div>
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Satellites")}
                           </div>
                           <div className="text-sm">{data?.satellites?.join(", ")}</div>
                        </div>
                     </div>
                     {/* Thông tin đặt ảnh */}
                     <Section title={t("General Information Request")}>
                        <Row
                           label={t("Order name")}
                           value={data?.items?.[0]?.request_body?.order_name ?? "--"}
                        />
                        <Row
                           label={t("Reason")}
                           value={data?.items?.[0]?.request_body?.reason}
                        />
                        <Row
                           label={t("Priority")}
                           value={t(data?.items?.[0]?.request_body?.priority)}
                           highlight
                        />
                        <Row
                           label={t("Description")}
                           value={data?.items?.[0]?.request_body?.description_order}
                        />
                        {data?.items?.[0]?.company_price &&
                           data?.items?.[0]?.company_price !== undefined && (
                              <>
                                 <Divider />
                                 <Row
                                    label={t("Price")}
                                    highlight
                                    value={
                                       data?.items?.[0]?.company_price
                                          ? new Intl.NumberFormat("en-US", {
                                             style: "currency",
                                             currency:
                                                data?.items?.[0]?.currency_company || "USD",
                                          }).format(data?.items?.[0]?.company_price)
                                          : "--"
                                    }
                                 />
                              </>
                           )}

                        {data?.notes && data?.notes !== undefined && (
                           <>
                              <Divider />
                              <Row label={t("Notes")} warning value={data?.notes} />
                           </>
                        )}
                     </Section>
                     {/* Thông số chụp ảnh */}
                     <Section title={t("Order form")}>
                        {data?.items?.length > 0 &&
                           data?.items?.[0]?.request_body &&
                           Object.entries(data?.items?.[0]?.request_body)?.length >
                           0 && (
                              <>
                                 {Object.entries(data?.items?.[0]?.request_body)
                                    ?.filter(
                                       (itemF?: any) => !dataKeyGeneral.includes(itemF[0]),
                                    )
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    ?.map(([key, value]) => (
                                       <Row
                                          key={key}
                                          label={formatLabelWithUnit(key, t)}
                                          value={
                                             typeof value === "object"
                                                ? JSON.stringify(value)
                                                : formatValue(value, key)
                                          }
                                       />
                                    ))}
                              </>
                           )}
                     </Section>
                     {data?.items?.[0]?.stac_items?.length > 0 && <Section title={t("Images in order")}>
                        {data?.items?.[0]?.stac_items?.map((image: any) => <CartImageOrder item={image} map={engineMap} handleViewInfo={() => handleViewInfo(image)} />)}
                     </Section>}
                     {/* Thông tin đa giác */}
                     <Section title={t("Polygon information")}>
                        <PolygonInfo
                           selectedPolygon={data?.items?.[0]?.order_payload?.geometry}
                           t={t}
                        />
                     </Section>
                  </div>

                  {(data?.status?.key === "NEWORDER" ||
                     data?.status?.key === "CONFIRMING_QUOTATION" ||
                     data?.status?.key === "CLOSED") && (
                        <div
                           className="flex absolute bottom-0 w-full justify-between px-6  py-4"
                           style={{ borderTop: "1px solid var(--divider-1)" }}
                        >
                           {data?.status?.key === "NEWORDER" && (
                              <>
                                 <div>
                                    <span className="w-[151px] block">
                                       <Button
                                          onClick={() => toggleCancelOrder()}
                                          className="w-full !bg-[none] border border-[#FA5343] text-[#FA5343]"
                                          style={{ background: "none" }}
                                          disabled={loading}
                                       >
                                          {t("Cancel request")}
                                       </Button>
                                    </span>
                                 </div>
                                 <div className="flex gap-2">
                                    <span className="block">
                                       <Button
                                          className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                                          style={{ background: "none" }}
                                          onClick={() => navigate(`./edit${location.search}`)}
                                          disabled={loading}
                                       >
                                          {t("Edit")}
                                       </Button>
                                    </span>
                                    <span className="w-[151px] block">
                                       <Button
                                          onClick={() => toggleSendOrderUser()}
                                          className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                          disabled={loading}
                                       >
                                          {t("Send request")}
                                       </Button>
                                    </span>
                                 </div>
                              </>
                           )}

                           {data?.status?.key === "CONFIRMING_QUOTATION" && (
                              <>
                                 <div className="flex gap-2 justify-end w-full">
                                    <span className="block">
                                       <Button
                                          className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                                          style={{ background: "none" }}
                                          onClick={() => toggleCancelPrice()}
                                          disabled={loading}
                                       >
                                          {t("Cancel quotation")}
                                       </Button>
                                    </span>
                                    <span className="w-[151px] block">
                                       <Button
                                          onClick={() => toggleConfirmPrice()}
                                          className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                          disabled={loading}
                                       >
                                          {t("Accept quotation")}
                                       </Button>
                                    </span>
                                 </div>
                              </>
                           )}

                           {data?.status?.key === "CLOSED" && data?.items?.[0]?.stac_items?.length > 0 && (
                              <div className="flex gap-2 justify-end w-full">
                                 <span className="w-[151px] block">
                                    <Button
                                       onClick={() => handleDownloadData()}
                                       className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                       disabled={loading || downloadingFiles.length > 0}
                                    >
                                       {t("Download data")}
                                    </Button>
                                 </span>
                              </div>
                           )}
                        </div>
                     )}
               </Spin>
            </div>

            <div className="flex-1 w-7/12">
               {selectedBasemap && (
                  <ImagesMapVMRS
                     mapType={mapType}
                     view={mapView}
                     onSetEngineMap={(map: any) =>
                     {
                        if (!map) return;
                        // setEngineMap only after map is fully loaded so fly2GeometryList has a ready engine
                        map.loaded()
                           ? setEngineMap(map)
                           : map.once("load", () => setEngineMap(map));
                     }}
                  />
               )}
            </div>

            {shouldRenderCancelOrder && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openCancelOrder}
                  width={"450px"}
                  toggle={toggleCancelOrder}
                  handleConfirm={() =>
                  {
                     if (reason?.trim().length > 0)
                     {
                        handleCancel();
                     }
                  }}
                  disabled={reason?.trim().length === 0}
               >
                  <p className="mb-1">
                     {t("Are you sure you want to cancel this image's request?")}
                  </p>
                  <ReasonCancelReject reason={reason} setReasson={setReason} t={t} />
               </CommonComponents.ModalUI>
            )}

            {shouldSendOrderUser && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openSendOrderUser}
                  toggle={toggleSendOrderUser}
                  handleConfirm={handleSendOrderUser}
               >
                  <p>
                     {t(
                        "Are you sure you want to send this image's request to admin?",
                     )}
                  </p>
               </CommonComponents.ModalUI>
            )}

            {shouldConfirmPrice && (
               <CommonComponents.ModalUI
                  title={t("Confirm quotation")}
                  open={openConfirmPrice}
                  toggle={toggleConfirmPrice}
                  handleConfirm={handleConfirmPrice}
               >
                  <p>{t("Are you sure you want to confirm price's this order?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldCancelPrice && (
               <CommonComponents.ModalUI
                  title={t("Cancel order")}
                  open={openCancelPrice}
                  toggle={toggleCancelPrice}
                  handleConfirm={handleCancelPrice}
               >
                  <p>{t("Are you sure you want to cancel this order?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldRenderViewInfo && (
               <CommonComponents.ModalUI
                  open={openViewInfo}
                  title={t("Information")}
                  toggle={() =>
                  {
                     toggleViewInfo();
                  }}
                  handleConfirm={() => { }}
                  hiddenFooter={true}
                  width={1120}
               >

                  <ImageInfo data={selectedView} open={openViewInfo} />

               </CommonComponents.ModalUI>
            )}
         </div>
      </ContentLayoutManagement>
   );
}
